import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type {
  EventEngagement,
  InterestRung,
  InterestSource,
} from '@@/types/api';
import { useEventInterestApi } from '~/composables/api/use-event-interest-api';
import { useAuthStore } from '~/stores/use-auth-store';

/**
 * Single authoritative copy of the viewer's declared interests, shared by event
 * cards, Almanac surfaces, and the dashboard. Cards seed their button state from
 * `rungByEventId`; `localSet` applies optimistic upserts from the interest
 * orchestration composable.
 *
 * SSR-safe: the network-backed `ensure*` actions bail on the server and when
 * unauthenticated, so the store is inert until a signed-in client uses it.
 */
export const useMyInterests = defineStore('myInterests', () => {
  const upcoming = ref<EventEngagement[]>([]);
  const past = ref<EventEngagement[]>([]);
  const loadedUpcoming = ref(false);
  const loadedPast = ref(false);

  // O(1) rung lookup for seeding button state on cards.
  const rungByEventId = computed(
    () => new Map(upcoming.value.map((e) => [e.event_id, e.rung])),
  );

  const interestApi = useEventInterestApi();
  const authStore = useAuthStore();

  // Dedupe concurrent ensureUpcoming() calls — one network request in flight.
  let inflightUpcoming: Promise<void> | null = null;

  async function ensureUpcoming(force = false): Promise<void> {
    if (import.meta.server) return;
    if (!authStore.isAuthenticated) return;
    if (loadedUpcoming.value && !force) return;
    if (inflightUpcoming) return inflightUpcoming;

    inflightUpcoming = (async () => {
      try {
        const rows = await interestApi.getMyEvents('upcoming');
        upcoming.value = rows;
        loadedUpcoming.value = true;
      } catch (e) {
        // A failed background load must never throw into render.
        Sentry.captureException(e, {
          tags: { operation: 'ensure_upcoming_interests' },
        });
      } finally {
        inflightUpcoming = null;
      }
    })();

    return inflightUpcoming;
  }

  async function ensurePast(): Promise<void> {
    if (import.meta.server) return;
    if (!authStore.isAuthenticated) return;
    if (loadedPast.value) return;

    try {
      const rows = await interestApi.getMyEvents('past');
      past.value = rows;
      loadedPast.value = true;
    } catch (e) {
      Sentry.captureException(e, {
        tags: { operation: 'ensure_past_interests' },
      });
    }
  }

  /**
   * Optimistic upsert of an interest row into `upcoming`. When `rung` is null the
   * row is removed; otherwise the row is inserted or updated, preserving existing
   * fields, merging `meta`, and setting the authoritative `rung`.
   */
  function localSet(
    eventId: string,
    source: InterestSource,
    rung: InterestRung | null,
    meta?: Partial<EventEngagement>,
  ): void {
    if (rung === null) {
      upcoming.value = upcoming.value.filter((e) => e.event_id !== eventId);
      return;
    }

    const existing = upcoming.value.find((e) => e.event_id === eventId);
    const row: EventEngagement = {
      title: existing?.title ?? '',
      kind: existing?.kind ?? '',
      point: existing?.point ?? null,
      timing_window: existing?.timing_window ?? null,
      timing_display: existing?.timing_display ?? null,
      status: existing?.status ?? 'upcoming',
      spawned_event_id: existing?.spawned_event_id ?? null,
      ...meta,
      // Authoritative identity/rung always win over merged meta.
      source,
      event_id: eventId,
      rung,
    };

    upcoming.value = existing
      ? upcoming.value.map((e) => (e.event_id === eventId ? row : e))
      : [...upcoming.value, row];
  }

  function reset(): void {
    upcoming.value = [];
    past.value = [];
    loadedUpcoming.value = false;
    loadedPast.value = false;
    inflightUpcoming = null;
  }

  // Clear cached interests on logout. Wired here (not in the auth store) so the
  // foundational auth store keeps NO reverse dependency on this store — avoiding
  // a circular store import. The watcher only exists once this store has been
  // instantiated, which is exactly when there is cached state worth clearing.
  watch(
    () => authStore.isAuthenticated,
    (isAuth, wasAuth) => {
      if (wasAuth && !isAuth) reset();
    },
    { flush: 'sync' },
  );

  return {
    upcoming,
    past,
    loadedUpcoming,
    loadedPast,
    rungByEventId,
    ensureUpcoming,
    ensurePast,
    localSet,
    reset,
  };
});
